import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { hostedUILogout } from '../utils/cognitoAuth';

const pool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_USER_POOL_ID,
  ClientId:   process.env.REACT_APP_USER_POOL_CLIENT_ID,
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [token, setToken]     = useState(null);
  const [loading, setLoading] = useState(true);

  const buildUser = useCallback((session) => {
    const payload = session.getIdToken().decodePayload();
    const groups  = payload['cognito:groups'] || [];
    if (!groups.includes('admins')) return null;
    setToken(session.getIdToken().getJwtToken());
    return {
      id:        payload.sub,
      email:     payload.email,
      firstName: payload.given_name  || '',
      lastName:  payload.family_name || '',
      isAdmin:   true,
    };
  }, []);

  const loadSession = useCallback(() => new Promise((resolve) => {
    const current = pool.getCurrentUser();
    if (!current) { resolve(null); return; }
    current.getSession((err, session) => {
      if (err || !session?.isValid()) { resolve(null); return; }
      const u = buildUser(session);
      setUser(u);
      resolve({ session, returnPath: '/dashboard' });
    });
  }), [buildUser]);

  useEffect(() => {
    loadSession().finally(() => setLoading(false));
  }, [loadSession]);

  // Called from CallbackPage after tokens are stored in localStorage.
  // Re-runs the session check so the app recognises the newly stored tokens.
  const refreshSession = useCallback(async () => {
    const result = await loadSession();
    return result;
  }, [loadSession]);

  const logout = () => {
    setUser(null);
    setToken(null);
    hostedUILogout(); // clears localStorage tokens + Cognito session cookie
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, logout, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
